import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { usePageMeta } from "../lib/meta";
import { Reveal, SectionDivider, ShareButton } from "../components/Chrome";
import { ReviewPanel, Stars, launchApp, NotifyMe } from "./AppsShowcase";

export default function AppDetail() {
  const { id } = useParams();
  const { data: app, isError } = useQuery({
    queryKey: ["app", id],
    queryFn: async () => (await api.get(`/apps/${id}`)).data,
    retry: false,
  });
  usePageMeta(app ? `${app.name} — Klyx™ Apps` : "Klyx™ Apps", app?.description);

  if (isError)
    return (
      <div data-testid="app-not-found" className="min-h-screen flex flex-col items-center justify-center gap-6">
        <p className="text-zinc-200">This app doesn't exist or was removed.</p>
        <Link to="/apps" className="text-mauve-light text-sm tracking-[0.2em] uppercase font-mono hover:text-white transition-colors duration-200">
          ← Back to all apps
        </Link>
      </div>
    );
  if (!app) return <div data-testid="app-detail-loading" className="min-h-screen bg-ink" />;

  return (
    <div data-testid={`app-detail-page-${app.id}`} className="min-h-screen">
      <section className="pt-32 lg:pt-44 pb-16 spotlight">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Link to="/apps" data-testid="app-detail-back" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-zinc-300 hover:text-mauve-light transition-colors duration-200 mb-10">
            <ArrowLeft size={14} /> All apps
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <Reveal className="lg:col-span-6">
              <div className="aspect-[16/10] overflow-hidden border border-white/10 bg-surface">
                {app.image ? (
                  <img src={app.image} alt={app.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-heading font-black text-6xl text-white/10">{app.name?.[0]}</div>
                )}
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-6">
              <div className="flex flex-wrap gap-2 mb-5">
                {app.tags?.map((t) => (
                  <span key={t} className="font-mono text-[10px] tracking-[0.2em] uppercase text-mauve-light border border-mauve/40 px-2 py-0.5">
                    {t}
                  </span>
                ))}
              </div>
              <h1 data-testid="app-detail-name" className="font-heading font-black text-4xl sm:text-6xl tracking-tighter text-silver">{app.name}</h1>
              <div className="mt-4 flex items-center gap-3">
                <Stars value={app.avg_rating} size={18} testPrefix="app-detail-stars" />
                <span className="text-sm text-zinc-300">
                  {app.avg_rating > 0 ? `${app.avg_rating} / 5` : "Not rated yet"} · {app.rating_count} rating{app.rating_count === 1 ? "" : "s"}
                </span>
              </div>
              <p className="mt-6 text-base sm:text-lg text-zinc-200 leading-relaxed">{app.description}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                {app.coming_soon ? (
                  <>
                    <span data-testid="app-detail-coming-soon" className="inline-flex items-center gap-3 border border-mauve/40 bg-mauve/10 text-mauve-light px-8 py-4 text-xs tracking-[0.2em] uppercase font-bold">
                      Coming soon
                    </span>
                    <NotifyMe app={app} size="lg" />
                  </>
                ) : (
                  <button
                    data-testid="app-detail-launch"
                    onClick={() => launchApp(app)}
                    className="inline-flex items-center gap-3 bg-mauve text-white px-8 py-4 text-xs tracking-[0.2em] uppercase font-bold hover:bg-mauve-dark transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
                  >
                    Launch app <ArrowUpRight size={16} />
                  </button>
                )}
                <ShareButton title={`${app.name} — Klyx™ Apps`} testId="app-share-button" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {app.screenshots?.length > 0 && (
        <section data-testid="app-screenshots" className="bg-surface">
          <SectionDivider />
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20">
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-mauve-light mb-8">Screenshots</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {app.screenshots.map((s, i) => (
                <Reveal key={s} delay={(i % 3) * 0.08}>
                  <div className="aspect-[16/10] overflow-hidden border border-white/10">
                    <img src={s} alt={`${app.name} screenshot ${i + 1}`} className="w-full h-full object-cover" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-elevated">
        <SectionDivider />
        <div className="max-w-[800px] mx-auto px-6 lg:px-12 py-20">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-mauve-light mb-2">Rate &amp; review</p>
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-silver mb-8">What do you think of {app.name}?</h2>
          <ReviewPanel app={app} />
        </div>
      </section>
    </div>
  );
}
